import { AlertTriangle, BookOpenCheck, Check, CheckCircle2, GitFork, GitPullRequest, Lightbulb, Redo2, Undo2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { Scene } from "../data/story";
import { useStoryPreferences } from "../i18n";

type ManuscriptEditorProps = {
  scene: Scene;
  content: string[];
  saved: boolean;
  readOnly?: boolean;
  onEdit: (content: string) => void;
  onPropose?: () => void;
  canPropose?: boolean;
  proposing?: boolean;
  facts?: string[];
  invalidationCount?: number;
  relationshipCount?: number;
  eventCount?: number;
  beliefCount?: number;
  onOpenWorld?: () => void;
  onOpenContext?: () => void;
};

export function ManuscriptEditor({ scene, content, saved, readOnly = false, onEdit, onPropose, canPropose = false, proposing = false, facts = [], invalidationCount = 0, relationshipCount = 0, eventCount = 0, beliefCount = 0, onOpenWorld, onOpenContext }: ManuscriptEditorProps) {
  const { t } = useStoryPreferences();
  const editorRef = useRef<HTMLDivElement>(null);
  const writingRef = useRef<HTMLElement>(null);
  const [focusMode, setFocusMode] = useState(false);
  const [selectedWords, setSelectedWords] = useState(0);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || document.activeElement === editor) return;
    const fragment = document.createDocumentFragment();
    content.forEach((paragraph, index) => {
      const element = document.createElement("p");
      element.textContent = paragraph;
      if (index === 2) element.className = "focused-paragraph";
      fragment.append(element);
    });
    editor.replaceChildren(fragment);
  }, [content, scene.id]);

  useEffect(() => {
    const updateSelection = () => {
      const selection = document.getSelection();
      if (!selection || !editorRef.current?.contains(selection.anchorNode)) {
        setSelectedWords(0);
        return;
      }
      setSelectedWords(Array.from(selection.toString().replace(/\s/gu, "")).length);
    };
    document.addEventListener("selectionchange", updateSelection);
    return () => document.removeEventListener("selectionchange", updateSelection);
  }, []);

  function history(command: "undo" | "redo") {
    if (readOnly) return;
    editorRef.current?.focus();
    document.execCommand(command);
  }

  const metrics = [
    [BookOpenCheck, facts.length, t("canonFacts")], [GitFork, eventCount, t("timelineEventsCount")], [Lightbulb, beliefCount, t("beliefsCount")],
    [GitFork, relationshipCount, t("relationships")], [AlertTriangle, invalidationCount, t("conflictsCount")],
  ] as const;

  return (
    <main className={`manuscript-panel${focusMode ? " is-focus" : ""}`} id="write">
      <div className="manuscript-scroll">
        <article className="manuscript-overview">
          <header className="manuscript-hero">
            <div><span className="chapter-label">{t("write")}</span><h1>{scene.title}</h1><p>{t("draftCanon")}</p></div>
            <div className="hero-status"><span><Check />{saved ? t("saved") : t("saving")}</span><span>{content.join("").length.toLocaleString()} {t("words")}</span></div>
          </header>
          <details className="manuscript-context"><summary>{t("currentContext")}</summary>
          <section className="story-metrics" aria-label={t("currentContext")}>
            {metrics.map(([Icon, value, label]) => <article key={label}><Icon aria-hidden="true" /><strong>{value}</strong><span>{label}</span></article>)}
          </section>
          <div className="story-overview-grid">
            <section className="overview-card canon-overview"><h2><BookOpenCheck />{t("canonFacts")}</h2><p>{t("canonDelta")}</p><ul>{facts.slice(0,2).map((fact) => <li key={fact}><CheckCircle2 />{fact}</li>)}</ul>{facts.length === 0 ? <p className="overview-empty">{t("noFacts")}</p> : null}<button type="button" onClick={onOpenWorld}>{t("allFacts")} <span>→</span></button></section>
            <section className="overview-card relationship-overview"><h2><GitFork />{t("relationships")}</h2><strong>{relationshipCount > 0 ? relationshipCount : t("noConflicts")}</strong><p>{t("conflictHint")}</p><button type="button" onClick={onOpenWorld}>{t("checkContinuity")} <span>→</span></button></section>
            <section className="overview-card conflict-overview"><h2><AlertTriangle />{t("potentialConflicts")}</h2><strong>{invalidationCount > 0 ? `${invalidationCount} ${t("conflictsCount")}` : t("noPotentialConflicts")}</strong><p>{t("continuityHint")}</p><button type="button" onClick={() => writingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}>{t("continueWriting")} <span>→</span></button></section>
          </div>
          </details>
        </article>
        <section className="writing-canvas" ref={writingRef}>
          <div className="writing-canvas-heading"><div><span className="chapter-label">DRAFT</span><h2>{scene.title}</h2></div><p className="plain-text-note">{t("plainTextEditor")}</p></div>
          <div
            ref={editorRef}
            className="manuscript-content"
            contentEditable={!readOnly}
            aria-readonly={readOnly}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label={t("write")}
            onInput={(event) => onEdit(event.currentTarget.innerText)}
          />
          <div className="word-count">{content.join("").length} {t("words")}</div>
        </section>
      </div>
      <div className="editor-toolbar desktop-editor-toolbar" aria-label={t("editorTools")}>
        <div className="format-actions">
          <button type="button" aria-label={t("undo")} onClick={() => history("undo")}><Undo2 /></button><button type="button" aria-label={t("redo")} onClick={() => history("redo")}><Redo2 /></button>
        </div>
        <div className="editor-footer-state"><label>{t("focusMode")}<input type="checkbox" checked={focusMode} onChange={(event) => setFocusMode(event.target.checked)} /></label><span>{selectedWords} {t("selectedWords")}</span></div>
        <div className="editor-command-actions"><button type="button" disabled={!canPropose || proposing} onClick={onPropose}><GitPullRequest />{proposing ? t("submitting") : t("submitReview")}</button>{onOpenContext ? <button type="button" onClick={onOpenContext}><Lightbulb />{t("contextAi")}</button> : null}<button type="button" onClick={onOpenWorld}><BookOpenCheck />{t("world")}</button></div>
      </div>
      <div className="mobile-format-toolbar" aria-label={t("editorTools")}>
        <button type="button" aria-label={t("undo")} onClick={() => history("undo")}><Undo2 /></button>
        <button type="button" aria-label={t("redo")} onClick={() => history("redo")}><Redo2 /></button>
        {onOpenContext ? <button type="button" aria-label={t("contextAi")} onClick={onOpenContext}><Lightbulb /></button> : null}
        <button type="button" aria-label={t("world")} onClick={onOpenWorld}><BookOpenCheck /></button>
      </div>
    </main>
  );
}
